import React, { useEffect, useMemo, useState } from 'react';
import { investorApi, investorLocalizationApi } from '@/api';
import { Button, Input, CodeCountdown } from '@/components';
import { Container, BREAKPOINTS, Flex } from '@/ui';
import { COLORS } from '@/utils/static';
import { useDispatch, useSelector } from 'react-redux';
import { investorActions } from '@/store/investor';
import { Form, Typography } from 'antd';
import { Text } from '@/ui/typography';
import { useTranslation } from 'react-i18next';
import { useCountries, usePopups } from '@/hooks';
import styled from 'styled-components';
import ChangeEmailOrPhone from '@/components/registrationComponents/ChangeEmailOrPhone';

export const Step2 = ({ goNext }) => {
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showErrorToast, showInfoToast } = usePopups();
  const { email } = useSelector((state) => state.investor);
  const { languages } = useCountries();
  const [code, setCode] = useState('');
  const [triggerSendCode, setTriggerSendCode] = useState(null);

  useEffect(() => {
    setLanguageForEmails().then(() => {
      handleSendCode();
    });
  }, []);

  const handleSendCode = () => {
    investorApi
      .sendEmailCode()
      .then()
      .catch((e) => {
        console.error(e);
        showErrorToast(e, true);
      });
  };

  const setLanguageForEmails = async () => {
    const lang = languages.find((i) => i.code === i18n.language.toUpperCase());

    return lang ? await investorLocalizationApi.setLanguage({ languageId: lang.id }) : null;
  };

  const handleNext = async () => {
    try {
      const res = await investorApi.verifyEmail(code);

      if (res.status === 200) {
        const { verifiedEmail } = res.data;
        dispatch(investorActions.setVerifiedEmail(verifiedEmail));

        goNext();
      }
    } catch (e) {
      showInfoToast(e, true);
    }
  };

  const disableButton = useMemo(() => {
    return !code;
  }, [code]);

  return (
    <>
      <StyledRow vertical align='center'>
        <Text type='secondary'>{t('The verification code has been sent on your e-mail:')}</Text>
        <Flex gap={10} align='center' wrap>
          <Text $color={COLORS.blueAntd}>{email}</Text>
          <ChangeEmailOrPhone
            label={'email'}
            setTriggerSendCode={() =>
              setTriggerSendCode((prevValue) => (prevValue ? prevValue + 1 : 1))
            }
          />
        </Flex>
      </StyledRow>

      <Form layout='vertical' style={{ maxWidth: '600px', width: '100%' }}>
        <StyledVerificationBlock $wide $marginTop={60}>
          <Input
            label={t('Verification code')}
            required
            placeholder={t('Enter code')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            boldLabel
          />
        </StyledVerificationBlock>

        <Container $wide>
          <StyledNextRow justify={'space-between'} $wide wrap>
            <Container $maxWidth={120} $wide>
              <Button
                type='filled'
                size={window.innerWidth <= BREAKPOINTS.sm ? 'medium' : 'large'}
                text={t('Next')}
                onClick={handleNext}
                disabled={disableButton}
              />
            </Container>

            <CodeCountdown
              triggerSendCode={triggerSendCode}
              sendCode={investorApi.sendEmailCode}
              isInitialCall={false}
            />
          </StyledNextRow>
        </Container>
      </Form>
    </>
  );
};

const StyledRow = styled(Flex)`
  margin-top: 24px;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    margin-top: 48px;
  }
`;

const StyledVerificationBlock = styled(Container)`
  @media (max-width: ${BREAKPOINTS.smd}px) {
    margin-top: 24px;
  }
`;

const StyledNextRow = styled(Flex)`
  margin-top: 70px;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    margin-top: 40px;
  }
`;
